import { roundClock } from './round-config'

// "Running late / early" (worker Today card) — the pure half. A worker one
// tap away from a job tells the customer from their phone's own Messages app
// (Roundly never sends an SMS itself): the phone number becomes an `sms:` link,
// the wording is composed here, and the visit gets a `⏱` note line saying the
// customer was told. Everything below is free of DOM/server dependencies so
// the wording, the link and the note line can be pinned by a test verbatim.

// "Chris Smith (outram)" → "Chris"; a trailing bracketed alias is a household
// note and must never reach the customer's phone. A name that is ONLY an alias
// ("(outram)") falls back to the alias itself — still better than an empty
// greeting.
export function firstName(raw: string | null | undefined): string {
  const name = String(raw ?? '').trim()
  if (!name) return ''
  const withoutAlias = name.replace(/\([^)]*\)/g, ' ').trim()
  const base = withoutAlias || name.replace(/[()]/g, '').trim()
  // "Mr Anthony Haeri" greets Anthony, not Mr; "Mr and Mrs Smith" greets Smith
  // (the surname, the only name we have) rather than "Mr".
  const words = base.split(/\s+/).filter((w) => !/^(mr|mrs|ms|miss|dr|mr\.|mrs\.|ms\.|dr\.|and|&)$/i.test(w))
  return words[0] ?? base.split(/\s+/)[0] ?? ''
}

export interface LateMessageParts {
  first: string
  workerName: string
  businessName: string
  minutes: number
  early: boolean
  // What the job is (review 2026-09-24): the text said "window clean" for
  // gutters too. Absent means windows, as before.
  kind?: 'window' | 'gutter' | 'other'
}

// Clamp whatever the "other" box produced to something a sentence survives.
function normaliseMinutes(minutes: number): number {
  return Math.min(240, Math.max(1, Math.round(Number(minutes) || 0)))
}

// The owner tunes wording here and nowhere else.
export function lateMessage(p: LateMessageParts): string {
  const minutes = normaliseMinutes(p.minutes)
  const job = p.kind === 'gutter' ? 'gutter clean' : p.kind === 'other' ? 'visit' : 'window clean'
  if (p.early) {
    const start = p.kind === 'gutter' || p.kind === 'other' ? 'I can make a start early' : "I'll go ahead with the outside as usual"
    return `Hi ${p.first}, it's ${p.workerName} from ${p.businessName}. I'm running about ${minutes} minutes early today, ${start} unless you'd rather I came at the booked time.`
  }
  return `Hi ${p.first}, it's ${p.workerName} from ${p.businessName}. I'm running about ${minutes} minutes late for your ${job} today, sorry about that. See you soon.`
}

// Same number cleaning as ui.telHref (imported values are free text: "07000
// 000000 - NO WA"), but callable from this dependency-free module. Returns the
// digit string (leading + kept) or null when nothing plausible remains.
export function cleanPhone(raw: string | null | undefined): string | null {
  if (!raw) return null
  const digits = raw.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '')
  return digits.replace(/\D/g, '').length >= 7 ? digits : null
}

// iOS wants `sms:+44…&body=…`, Android `sms:+44…?body=…` — the separator is
// the only platform difference. The caller passes the userAgent so the link
// building stays testable (the screens pass navigator.userAgent).
// Only the FIRST number in the field, as WhatsApp does ("07700 900123 / 07700 900124" ran together
// into one long number; Astra review, 29 Sep 2026). Kept as typed: a text from a phone in the
// round's own country reaches a number written the local way, and no "no WA" note stops a text.
export function smsHref(raw: string | null | undefined, body: string, userAgent: string): string | null {
  const first = raw ? /\+?\d[\d\s().-]*\d/.exec(raw.replace(/\(0\)/g, '')) : null
  const digits = cleanPhone(first ? first[0] : null)
  if (!digits) return null
  const ios = /iPhone|iPad|iPod/.test(userAgent)
  return `sms:${digits}${ios ? '&' : '?'}body=${encodeURIComponent(body)}`
}

// The ⏱ note line reads the ROUND's clock whatever the phone's clock says (London for every
// UK round; round-config.ts, Step 1): the same clock the server stamps on a 🎙 voice line, so a
// visit's note reads as one timeline. (The voice line's format lives in
// functions/api/cleans/[id]/voice.ts.)
export function noteClock(now: Date): string {
  return roundClock(now)
}

export function lateNoteLine(now: Date, first: string, minutes: number, early: boolean): string {
  return `⏱ ${noteClock(now)} — told ${first} running ${normaliseMinutes(minutes)} min ${early ? 'early' : 'late'}`
}
